import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
    getAllMediaSpaces, 
    createMediaSpace, 
    updateMediaSpace, 
    deleteMediaSpace,
    getAllProperties 
} from '@/services/marketingService';
import * as Sentry from '@sentry/react';

const MediaSpacesPage = () => {
    const [mediaSpaces, setMediaSpaces] = useState([]);
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterProperty, setFilterProperty] = useState('all');
    const [filterActive, setFilterActive] = useState('all');
    const [formData, setFormData] = useState({
        propertyId: '',
        name: '',
        locationDescription: '',
        notes: '',
        isActive: true
    });

    const { apiFetch } = useAuth();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [spacesData, propertiesData] = await Promise.all([
                getAllMediaSpaces(apiFetch),
                getAllProperties(apiFetch)
            ]);
            setMediaSpaces(spacesData);
            setProperties(propertiesData);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (space = null) => {
        if (space) {
            setSelectedSpace(space);
            setFormData({
                propertyId: space.propertyId || '',
                name: space.name || '',
                locationDescription: space.locationDescription || '',
                notes: space.notes || '',
                isActive: space.isActive ?? true
            });
        } else {
            setSelectedSpace(null);
            setFormData({
                propertyId: '',
                name: '',
                locationDescription: '',
                notes: '',
                isActive: true
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSpace(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.propertyId || !formData.name.trim()) {
            toast.error('Property and name are required');
            return;
        }

        setIsLoading(true);
        try {
            if (selectedSpace) {
                await updateMediaSpace(apiFetch, selectedSpace.mediaSpaceId, formData);
                toast.success('Media space updated successfully');
            } else {
                await createMediaSpace(apiFetch, formData);
                toast.success('Media space created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to save media space');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (space) => {
        setSelectedSpace(space);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedSpace) return;

        setIsLoading(true);
        try {
            await deleteMediaSpace(apiFetch, selectedSpace.mediaSpaceId);
            toast.success('Media space deleted successfully');
            setShowDeleteModal(false);
            setSelectedSpace(null);
            fetchData();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to delete media space');
        } finally {
            setIsLoading(false);
        }
    };

    const getPropertyName = (id) => {
        const prop = properties.find(p => (p.propertyID ?? p.PropertyID) === id);
        return prop?.propertyName || prop?.PropertyName || 'Unknown';
    };

    const filteredSpaces = mediaSpaces.filter(space => {
        const propertyName = getPropertyName(space.propertyId);
        const matchesSearch = space.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            propertyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            space.locationDescription?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesProperty = filterProperty === 'all' || 
                              space.propertyId === parseInt(filterProperty);
        
        const matchesFilter = filterActive === 'all' || 
                            (filterActive === 'active' && space.isActive) ||
                            (filterActive === 'inactive' && !space.isActive);

        return matchesSearch && matchesProperty && matchesFilter;
    });

    return (
        <div className="csi-container">
            <h1>Media Spaces</h1>

            <div className="toolbar">
                <div className="search-filter-group">
                    <input
                        type="text"
                        placeholder="Search media spaces..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control search-input"
                    />
                    <select
                        value={filterProperty}
                        onChange={(e) => setFilterProperty(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All Properties</option>
                        {properties.map((prop, index) => (
                            <option key={prop.propertyID ?? prop.PropertyID ?? `prop-${index}`} value={prop.propertyID ?? prop.PropertyID}>
                                {prop.propertyName || prop.PropertyName}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                </div>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    Add Media Space
                </button>
            </div>

            {isLoading ? (
                <div className="loading">Loading media spaces...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Property</th>
                                <th>Name</th>
                                <th>Location</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSpaces.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center' }}>
                                        No media spaces found
                                    </td>
                                </tr>
                            ) : (
                                filteredSpaces.map(space => (
                                    <tr key={space.mediaSpaceId}>
                                        <td>{getPropertyName(space.propertyId)}</td>
                                        <td>{space.name}</td>
                                        <td>{space.locationDescription || '-'}</td>
                                        <td>
                                            <span className={`badge ${space.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                                {space.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleOpenModal(space)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDeleteClick(space)}
                                                style={{ marginLeft: '5px' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>{selectedSpace ? 'Edit Media Space' : 'Add Media Space'}</h3>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="propertyId">Property *</label>
                                    <select
                                        id="propertyId"
                                        name="propertyId"
                                        value={formData.propertyId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">Select Property</option>
                                        {properties.map((prop, index) => (
                                            <option key={prop.propertyID ?? prop.PropertyID ?? `modal-prop-${index}`} value={prop.propertyID ?? prop.PropertyID}>
                                                {prop.propertyName || prop.PropertyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="name">Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="locationDescription">Location Description</label>
                                    <input
                                        type="text"
                                        id="locationDescription"
                                        name="locationDescription"
                                        value={formData.locationDescription}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes">Notes</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                        />
                                        {' '}Active
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseModal}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete {selectedSpace?.name}?</p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={handleDelete}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
</div>
    );
};

export default MediaSpacesPage;
